import type { JsonObject, JsonValue } from "@inf-monkeys-tech/monkeys";
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import {
  ChevronRight,
  Filter,
  ImageOff,
  Loader2,
  RefreshCw,
} from "lucide-react";

import {
  BaseBadge,
  BaseButton,
  BaseField,
  BaseInline,
  BaseInput,
  BaseMultiSelect,
  BaseStack,
  BaseTable,
  BaseTableBody,
  BaseTableCell,
  BaseTableContainer,
  BaseTableHead,
  BaseTableHeader,
  BaseTableRow,
  BaseToolbar,
} from "../base";
import { DataExplorerCollectionFooter } from "../data-explorer";
import {
  Badge,
  badgeVariants,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../primitives";
import { CapabilityStatus } from "./CapabilityStatus";
import type {
  CapabilityTranslate,
  DesignCapabilityRenderer,
} from "./contracts";
import { isJsonObject } from "./contracts";
import { DESIGN_CAPABILITY_I18N_KEYS } from "./i18n";

const JSON_SCHEMA_DIALECT = "https://json-schema.org/draft/2020-12/schema";
const MAX_MATRIX_COLUMNS = 64;
const MAX_MATRIX_WINDOW_ROWS = 200;
const MAX_CONTEXT_VALUES = 50;
const MAX_CONTEXT_PANELS = 50;
const MAX_VISIBLE_CONTEXT_VALUES = 3;
const MAX_SAFE_TEXT_LENGTH = 128;
const MAX_SAFE_JSON_DEPTH = 8;
const MAX_SAFE_JSON_ITEMS = 100;
const MAX_SAFE_JSON_PROPERTIES = 100;
const MAX_SAFE_JSON_STRING_LENGTH = 4096;

const strictSchema = (id: string, definition: JsonObject): JsonObject => ({
  $schema: JSON_SCHEMA_DIALECT,
  $id: id,
  ...definition,
});

const safeTextSchema: JsonObject = {
  type: "string",
  maxLength: MAX_SAFE_TEXT_LENGTH,
};

const safeIdSchema: JsonObject = {
  type: "string",
  minLength: 1,
  maxLength: MAX_SAFE_TEXT_LENGTH,
};

const localizedLabelSchema: JsonObject = {
  label: safeTextSchema,
  labelKey: safeIdSchema,
};

const safeScalarSchema: JsonObject = {
  type: ["string", "number", "boolean", "null"],
};

const safeJsonValueDefinitions = Object.fromEntries(
  Array.from({ length: MAX_SAFE_JSON_DEPTH + 1 }, (_, depth) => {
    const key = `safeJsonValue${depth}`;
    const scalar: JsonObject = {
      oneOf: [
        { type: "string", maxLength: MAX_SAFE_JSON_STRING_LENGTH },
        { type: "number" },
        { type: "boolean" },
        { type: "null" },
      ],
    };
    if (depth === 0) return [key, scalar];
    const childRef = { $ref: `#/$defs/safeJsonValue${depth - 1}` };
    return [
      key,
      {
        oneOf: [
          { $ref: "#/$defs/safeJsonValue0" },
          {
            type: "array",
            maxItems: MAX_SAFE_JSON_ITEMS,
            items: childRef,
          },
          {
            type: "object",
            maxProperties: MAX_SAFE_JSON_PROPERTIES,
            propertyNames: {
              type: "string",
              pattern: "^(?!(__proto__|constructor|prototype)$).{1,128}$",
            },
            additionalProperties: childRef,
          },
        ],
      },
    ];
  }),
) as JsonObject;

const safeJsonValueSchema: JsonObject = {
  $ref: `#/$defs/safeJsonValue${MAX_SAFE_JSON_DEPTH}`,
};

const withSafeJsonValueDefinitions = (schema: JsonObject): JsonObject => ({
  ...schema,
  $defs: safeJsonValueDefinitions,
});

export const ONTOLOGY_TREND_MATRIX_SCHEMA =
  "monkeys-design://ontology-view/trend-matrix/v1";
export const DATA_CONTEXT_BAR_SCHEMA =
  "monkeys-design://render-model/data-context-bar/v2";

export const ONTOLOGY_TREND_MATRIX_RENDER_MODEL_SCHEMA = strictSchema(
  ONTOLOGY_TREND_MATRIX_SCHEMA,
  {
    type: "object",
    additionalProperties: false,
    required: ["state", "columns", "rows", "window"],
    properties: {
      ...localizedLabelSchema,
      presentation: {
        type: "object",
        additionalProperties: false,
        required: ["kind"],
        properties: { kind: { enum: ["legacy-data-explorer"] } },
      },
      sources: {
        type: "array",
        maxItems: 4,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["id", "generatedImages"],
          properties: {
            id: safeIdSchema,
            sourceRecordId: safeIdSchema,
            generationRecordId: safeIdSchema,
            image: {
              type: "object",
              additionalProperties: false,
              required: ["url"],
              properties: {
                url: { type: "string", minLength: 1, maxLength: 4096 },
                thumbnailUrl: { type: "string", minLength: 1, maxLength: 4096 },
              },
            },
            generatedImages: {
              type: "array",
              maxItems: 4,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["url"],
                properties: {
                  url: { type: "string", minLength: 1, maxLength: 4096 },
                  thumbnailUrl: {
                    type: "string",
                    minLength: 1,
                    maxLength: 4096,
                  },
                  recordId: safeIdSchema,
                },
              },
            },
          },
        },
      },
      state: {
        type: "object",
        additionalProperties: false,
        required: ["kind"],
        properties: {
          kind: { enum: ["ready", "loading", "empty", "error"] },
          code: safeIdSchema,
        },
      },
      columns: {
        type: "array",
        maxItems: MAX_MATRIX_COLUMNS,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["id", "valueType"],
          properties: {
            id: safeIdSchema,
            ...localizedLabelSchema,
            valueType: { enum: ["number", "string", "boolean"] },
            align: { enum: ["left", "center", "right"] },
          },
        },
      },
      rows: {
        type: "array",
        maxItems: MAX_MATRIX_WINDOW_ROWS,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["id", "recordId", "cells"],
          properties: {
            id: safeIdSchema,
            recordId: safeIdSchema,
            ...localizedLabelSchema,
            cells: {
              type: "array",
              maxItems: MAX_MATRIX_COLUMNS,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["columnId", "value"],
                properties: {
                  columnId: safeIdSchema,
                  value: safeScalarSchema,
                  displayValue: safeTextSchema,
                },
              },
            },
          },
        },
      },
      selectedCell: {
        type: "object",
        additionalProperties: false,
        required: ["rowId", "columnId"],
        properties: { rowId: safeIdSchema, columnId: safeIdSchema },
      },
      window: {
        type: "object",
        additionalProperties: false,
        required: ["rowOffset", "rowCount", "totalRows"],
        properties: {
          rowOffset: { type: "integer", minimum: 0 },
          rowCount: {
            type: "integer",
            minimum: 0,
            maximum: MAX_MATRIX_WINDOW_ROWS,
          },
          totalRows: { type: "integer", minimum: 0 },
        },
      },
      pagination: {
        type: "object",
        additionalProperties: false,
        required: ["hasMore", "nextCursor", "loadingMore"],
        properties: {
          hasMore: { type: "boolean" },
          nextCursor: { type: ["string", "null"] },
          loadingMore: { type: "boolean" },
          errorCode: safeIdSchema,
          pageSize: { type: "integer", minimum: 1 },
          returned: { type: "integer", minimum: 0 },
          total: { type: "integer", minimum: 0 },
          loadedCount: { type: "integer", minimum: 0 },
          loadedStart: { type: "integer", minimum: 0 },
          loadedEnd: { type: "integer", minimum: 0 },
        },
      },
    },
  },
);

export const ONTOLOGY_TREND_MATRIX_SELECT_INTENT_SCHEMA = strictSchema(
  "monkeys-design://intent/ontology-trend-matrix-select/v1",
  {
    type: "object",
    additionalProperties: false,
    required: ["recordId"],
    properties: {
      recordId: safeIdSchema,
      rowId: safeIdSchema,
      columnId: safeIdSchema,
      value: safeScalarSchema,
    },
  },
);

const contextFieldNames = [
  "search",
  "tagIds",
  "tagGroupIds",
  "timeFrom",
  "timeTo",
  "clickedField",
  "clickedValue",
  "dashboardDrilldown",
] as const;

const contextFieldSchema: JsonObject = { enum: [...contextFieldNames] };
const stringListSchema: JsonObject = {
  type: "array",
  uniqueItems: true,
  maxItems: MAX_CONTEXT_VALUES,
  items: safeIdSchema,
};

const contextChoiceListSchema: JsonObject = {
  type: "array",
  maxItems: MAX_CONTEXT_VALUES,
  items: {
    type: "object",
    additionalProperties: false,
    required: ["id"],
    properties: {
      id: safeIdSchema,
      label: safeTextSchema,
      labelKey: safeIdSchema,
      disabled: { type: "boolean" },
    },
  },
};

const contextTagChoiceListSchema: JsonObject = {
  type: "array",
  maxItems: MAX_CONTEXT_VALUES,
  items: {
    type: "object",
    additionalProperties: false,
    required: ["id"],
    properties: {
      id: safeIdSchema,
      label: safeTextSchema,
      labelKey: safeIdSchema,
      disabled: { type: "boolean" },
      color: safeTextSchema,
      groupId: safeIdSchema,
      groupLabel: safeTextSchema,
      count: { type: "integer", minimum: 0 },
    },
  },
};

const contextTagPanelListSchema: JsonObject = {
  type: "array",
  maxItems: MAX_CONTEXT_PANELS,
  items: {
    type: "object",
    additionalProperties: false,
    required: ["id", "groups"],
    properties: {
      id: safeIdSchema,
      label: safeTextSchema,
      description: safeTextSchema,
      groups: {
        type: "array",
        maxItems: MAX_CONTEXT_VALUES,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["id", "tags"],
          properties: {
            id: safeIdSchema,
            label: safeTextSchema,
            description: safeTextSchema,
            selectMode: { enum: ["single", "multiple"] },
            tags: contextTagChoiceListSchema,
          },
        },
      },
    },
  },
};

const filterSnapshotProperties: JsonObject = {
  search: safeTextSchema,
  tagIds: stringListSchema,
  tagGroupIds: stringListSchema,
  timeFrom: safeTextSchema,
  timeTo: safeTextSchema,
  clickedField: safeTextSchema,
  clickedValue: safeJsonValueSchema,
  dashboardDrilldown: safeJsonValueSchema,
};

export const DATA_CONTEXT_BAR_RENDER_MODEL_SCHEMA =
  withSafeJsonValueDefinitions(
    strictSchema(DATA_CONTEXT_BAR_SCHEMA, {
      type: "object",
      additionalProperties: false,
      required: [...contextFieldNames],
      properties: {
        ...filterSnapshotProperties,
        presentation: { enum: ["inline", "compact", "legacy-data-explorer"] },
        disabled: { type: "boolean" },
        labelKey: safeIdSchema,
        ariaLabelKey: safeIdSchema,
        enabledFields: {
          type: "array",
          uniqueItems: true,
          maxItems: contextFieldNames.length,
          items: contextFieldSchema,
        },
        tagChoices: contextTagChoiceListSchema,
        viewTagChoices: contextTagChoiceListSchema,
        tagGroupChoices: contextChoiceListSchema,
        tagPanelChoices: contextTagPanelListSchema,
        viewScoped: { type: "boolean" },
      },
    }),
  );

export const DATA_CONTEXT_BAR_CLEAR_INTENT_SCHEMA =
  withSafeJsonValueDefinitions(
    strictSchema("monkeys-design://intent/data-context-bar-clear/v1", {
      type: "object",
      additionalProperties: false,
      required: ["clearedField", ...contextFieldNames],
      properties: {
        clearedField: { enum: ["all", ...contextFieldNames] },
        ...filterSnapshotProperties,
      },
    }),
  );

export const DATA_CONTEXT_BAR_CHANGE_INTENT_SCHEMA =
  withSafeJsonValueDefinitions(
    strictSchema("monkeys-design://intent/data-context-bar-change/v1", {
      type: "object",
      additionalProperties: false,
      required: ["changedField", ...contextFieldNames],
      properties: {
        changedField: contextFieldSchema,
        ...filterSnapshotProperties,
      },
    }),
  );

export const DATA_CONTEXT_BAR_REFRESH_INTENT_SCHEMA = strictSchema(
  "monkeys-design://intent/data-context-bar-refresh/v1",
  {
    type: "object",
    additionalProperties: false,
  },
);

const stringValue = (value: JsonValue | undefined) =>
  typeof value === "string" && value.trim() ? value : undefined;

const objectArray = (value: JsonValue | undefined): JsonObject[] =>
  Array.isArray(value) ? value.filter(isJsonObject) : [];

const stringArray = (value: JsonValue | undefined): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const scalarValue = (
  value: JsonValue | undefined,
): string | number | boolean | null | undefined =>
  value === null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"
    ? value
    : undefined;

const jsonScalar = (value: JsonValue | undefined): JsonValue =>
  scalarValue(value) === undefined ? null : scalarValue(value) ?? null;

const isSafeJsonValue = (
  value: JsonValue | undefined,
  depth = 0,
): value is JsonValue => {
  if (value === null || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string")
    return value.length <= MAX_SAFE_JSON_STRING_LENGTH;
  if (depth >= MAX_SAFE_JSON_DEPTH) return false;
  if (Array.isArray(value))
    return (
      value.length <= MAX_SAFE_JSON_ITEMS &&
      value.every((item) => isSafeJsonValue(item, depth + 1))
    );
  if (!isJsonObject(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= MAX_SAFE_JSON_PROPERTIES &&
    entries.every(
      ([key, item]) =>
        key.length > 0 &&
        key.length <= MAX_SAFE_TEXT_LENGTH &&
        !["__proto__", "constructor", "prototype"].includes(key) &&
        isSafeJsonValue(item, depth + 1),
    )
  );
};

const governedJsonValue = (value: JsonValue | undefined): JsonValue =>
  isSafeJsonValue(value) ? value : null;

const displayScalar = (value: JsonValue | undefined): string => {
  const scalar = scalarValue(value);
  if (scalar === null) return "—";
  if (scalar === undefined) return "";
  return String(scalar).slice(0, MAX_SAFE_TEXT_LENGTH);
};

const displayJsonSummary = (value: JsonValue | undefined): string => {
  const scalar = scalarValue(value);
  if (scalar !== undefined) return displayScalar(scalar);
  if (!isSafeJsonValue(value)) return "";
  try {
    return JSON.stringify(value).slice(0, MAX_SAFE_TEXT_LENGTH);
  } catch {
    return "";
  }
};

const translatedCopy = (
  value: JsonValue | undefined,
  key: JsonValue | undefined,
  translate: CapabilityTranslate,
  fallback: string,
): ReactNode => {
  const labelKey = stringValue(key);
  if (labelKey) return translate(labelKey);
  return stringValue(value) ?? fallback;
};

const cellValueMatchesColumn = (value: JsonValue, valueType: JsonValue) =>
  value === null ||
  (valueType === "number" && typeof value === "number") ||
  (valueType === "string" && typeof value === "string") ||
  (valueType === "boolean" && typeof value === "boolean");

const isValidTrendMatrix = (model: JsonObject) => {
  const columns = objectArray(model.columns);
  const rows = objectArray(model.rows);
  const window = isJsonObject(model.window) ? model.window : undefined;
  const columnIds = columns.flatMap((column) =>
    stringValue(column.id) ? [String(column.id)] : [],
  );
  const uniqueColumnIds = new Set(columnIds);
  if (
    columns.length > MAX_MATRIX_COLUMNS ||
    columnIds.length !== columns.length ||
    uniqueColumnIds.size !== columnIds.length ||
    rows.length > MAX_MATRIX_WINDOW_ROWS ||
    !window ||
    !Number.isInteger(window.rowOffset) ||
    !Number.isInteger(window.rowCount) ||
    !Number.isInteger(window.totalRows) ||
    Number(window.rowOffset) < 0 ||
    Number(window.rowCount) !== rows.length ||
    Number(window.totalRows) < Number(window.rowOffset) + rows.length
  )
    return false;
  const columnsById = new Map(
    columns.map((column) => [String(column.id), column]),
  );
  const rowIds = new Set<string>();
  for (const row of rows) {
    const rowId = stringValue(row.id);
    const recordId = stringValue(row.recordId);
    if (!rowId || !recordId || rowIds.has(rowId)) return false;
    rowIds.add(rowId);
    const cells = objectArray(row.cells);
    const cellColumnIds = new Set<string>();
    for (const cell of cells) {
      const columnId = stringValue(cell.columnId);
      const column = columnId ? columnsById.get(columnId) : undefined;
      if (
        !columnId ||
        !column ||
        cellColumnIds.has(columnId) ||
        !cellValueMatchesColumn(cell.value, column.valueType)
      )
        return false;
      cellColumnIds.add(columnId);
    }
  }
  return true;
};

const moveTrendCellFocus = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key))
    return;
  const matrix = event.currentTarget.closest("[data-trend-matrix]");
  if (!matrix) return;
  const buttons = Array.from(
    matrix.querySelectorAll<HTMLButtonElement>("button[data-trend-cell]"),
  );
  const currentIndex = buttons.indexOf(event.currentTarget);
  if (currentIndex < 0) return;
  const columnCount = Number(matrix.getAttribute("data-column-count"));
  const delta =
    event.key === "ArrowLeft"
      ? -1
      : event.key === "ArrowRight"
        ? 1
        : event.key === "ArrowUp"
          ? -columnCount
          : columnCount;
  const next = buttons[currentIndex + delta];
  if (!next) return;
  event.preventDefault();
  next.focus();
};

const LegacyTrendImageTile = ({
  image,
  placeholderLabel,
  onSelect,
}: {
  image?: JsonObject;
  placeholderLabel: string;
  onSelect?: () => void;
}) => {
  const src = stringValue(image?.thumbnailUrl) || stringValue(image?.url);
  const className = `relative flex h-[150px] w-[200px] shrink-0 overflow-hidden rounded-md border bg-muted/35${
    src ? "" : " border-dashed bg-muted/20"
  }${
    src && onSelect
      ? " cursor-pointer transition hover:border-primary/60 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
      : ""
  }`;
  const content = src ? (
    <img
      src={src}
      alt=""
      className="h-full w-full object-cover"
      loading="lazy"
    />
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
      <ImageOff className="h-5 w-5" />
      <span className="text-xs">{placeholderLabel}</span>
    </div>
  );
  return src && onSelect ? (
    <button type="button" className={className} onClick={onSelect}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
};

const LegacyTrendMatrix = ({
  model,
  loading,
  translate,
  emitIntent,
}: {
  model: JsonObject;
  loading?: boolean;
  translate: CapabilityTranslate;
  emitIntent: (type: string, payload?: JsonObject) => void;
}) => {
  const sources = objectArray(model.sources).slice(0, 4);
  if (loading && sources.length === 0) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {translate("components.data-v2.trend-matrix.loading")}
      </div>
    );
  }
  if (sources.length === 0) {
    return (
      <div
        className="flex h-full min-h-[320px] items-center justify-center px-6 text-center"
        role="status"
        aria-live="polite"
      >
        <div className="max-w-md space-y-2">
          <div className="text-base font-medium">
            {translate("components.data-v2.trend-matrix.empty-title")}
          </div>
          <div className="text-sm text-muted-foreground">
            {translate("components.data-v2.trend-matrix.empty-description")}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="h-full overflow-auto bg-background"
      data-legacy-trend-matrix
    >
      <div className="min-w-[1160px] divide-y">
        {sources.map((source, sourceIndex) => {
          const sourceId =
            stringValue(source.id) || `trend-current-${sourceIndex}`;
          const sourceRecordId = stringValue(source.sourceRecordId);
          const generated = objectArray(source.generatedImages).slice(0, 4);
          return (
            <div
              key={sourceId}
              className="grid w-max min-w-full items-center gap-5 px-6 py-4"
              style={{ gridTemplateColumns: "200px 40px repeat(4, 200px)" }}
            >
              <LegacyTrendImageTile
                image={isJsonObject(source.image) ? source.image : undefined}
                placeholderLabel={translate(
                  "components.data-v2.trend-matrix.no-source",
                )}
                onSelect={
                  sourceRecordId
                    ? () =>
                        emitIntent("capability.ontology-trend-matrix.select", {
                          recordId: sourceRecordId,
                        })
                    : undefined
                }
              />
              <div className="flex justify-center text-muted-foreground">
                <ChevronRight className="h-6 w-6" />
              </div>
              {Array.from({ length: 4 }, (_, index) => {
                const image = generated[index];
                const recordId =
                  stringValue(image?.recordId) ||
                  stringValue(source.generationRecordId);
                return (
                  <LegacyTrendImageTile
                    key={`${sourceId}-generated-${index}`}
                    image={image}
                    placeholderLabel={translate(
                      "components.data-v2.trend-matrix.no-generated",
                    )}
                    onSelect={
                      recordId
                        ? () =>
                            emitIntent(
                              "capability.ontology-trend-matrix.select",
                              { recordId },
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const OntologyTrendMatrixCapabilityRenderer: DesignCapabilityRenderer =
  ({
    model,
    childrenBySlot,
    translate,
    emitIntent,
    loading,
    empty,
    errorCode,
  }) => {
    const presentation = isJsonObject(model.presentation)
      ? stringValue(model.presentation.kind)
      : undefined;
    if (presentation === "legacy-data-explorer") {
      const sources = objectArray(model.sources);
      const pagination = isJsonObject(model.pagination)
        ? model.pagination
        : undefined;
      const loadedEnd =
        typeof pagination?.loadedEnd === "number"
          ? pagination.loadedEnd
          : sources.length;
      const loadedStart =
        typeof pagination?.loadedStart === "number"
          ? pagination.loadedStart
          : loadedEnd > 0
            ? 1
            : 0;
      const total =
        typeof pagination?.total === "number" ? pagination.total : loadedEnd;
      const pageSize =
        typeof pagination?.pageSize === "number" && pagination.pageSize > 0
          ? pagination.pageSize
          : 20;
      const loadedLabel =
        loadedStart > 0
          ? translate("components.data.card-view.loaded-window", {
              start: loadedStart,
              end: loadedEnd,
              total,
            })
          : translate("components.data.card-view.loaded-count", {
              loaded: loadedEnd,
              total,
            });
      return (
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1">
            <LegacyTrendMatrix
              model={model}
              loading={loading}
              translate={translate}
              emitIntent={emitIntent}
            />
          </div>
          <DataExplorerCollectionFooter
            selectedCount={0}
            loadedStart={loadedStart}
            loadedEnd={loadedEnd}
            total={total}
            pageSize={pageSize}
            loadedLabel={loadedLabel}
            pageSizeOptions={[20, 50, 100, 200, 500, 1000]}
            tone="hard-edge"
            classNames={{ summary: "min-w-max" }}
            onPageSizeChange={(nextPageSize) =>
              emitIntent("capability.ontology-trend-matrix.page-size-change", {
                pageSize: nextPageSize,
              })
            }
          />
          {childrenBySlot.footer}
        </div>
      );
    }
    const state = isJsonObject(model.state) ? model.state : undefined;
    const stateKind = stringValue(state?.kind);
    if (loading || stateKind === "loading")
      return <CapabilityStatus kind="loading" translate={translate} />;
    if (errorCode || stateKind === "error")
      return (
        <CapabilityStatus
          kind="error"
          code={errorCode ?? stringValue(state?.code)}
          translate={translate}
        />
      );
    const rows = objectArray(model.rows);
    if (empty || stateKind === "empty" || rows.length === 0)
      return (
        <div role="status" aria-live="polite">
          <CapabilityStatus kind="empty" translate={translate} />
        </div>
      );
    if (!isValidTrendMatrix(model))
      return (
        <CapabilityStatus
          kind="error"
          code="capability.ontology-trend-matrix.invalid-model"
          translate={translate}
        />
      );

    const columns = objectArray(model.columns);
    const selectedCell = isJsonObject(model.selectedCell)
      ? model.selectedCell
      : undefined;
    const window = model.window as JsonObject;
    const rowOffset = Number(window.rowOffset);
    const totalRows = Number(window.totalRows);
    const pagination = isJsonObject(model.pagination)
      ? model.pagination
      : undefined;
    const pageSize =
      typeof pagination?.pageSize === "number" &&
      Number.isSafeInteger(pagination.pageSize) &&
      pagination.pageSize > 0
        ? pagination.pageSize
        : Math.max(1, rows.length);
    return (
      <BaseStack gap="var(--global-spacing)">
        {childrenBySlot.header}
        <BaseTableContainer
          data-trend-matrix
          data-column-count={columns.length}
          data-window-row-offset={rowOffset}
          data-window-row-count={rows.length}
          data-total-rows={totalRows}
        >
          <BaseTable
            aria-label={String(
              translatedCopy(
                model.label,
                model.labelKey,
                translate,
                translate(
                  DESIGN_CAPABILITY_I18N_KEYS.ontologyTrendMatrix.label,
                ),
              ),
            )}
          >
            <caption>
              {translate(
                DESIGN_CAPABILITY_I18N_KEYS.ontologyTrendMatrix.window,
                {
                  start: rowOffset + 1,
                  end: rowOffset + rows.length,
                  total: totalRows,
                },
              )}
            </caption>
            <BaseTableHeader>
              <BaseTableRow>
                <BaseTableHead
                  label={translate(
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyTrendMatrix.row,
                  )}
                />
                {columns.map((column) => {
                  const columnId = String(column.id);
                  return (
                    <BaseTableHead
                      key={columnId}
                      columnId={columnId}
                      label={translatedCopy(
                        column.label,
                        column.labelKey,
                        translate,
                        columnId,
                      )}
                      align={
                        (stringValue(column.align) as
                          | "left"
                          | "center"
                          | "right"
                          | undefined) ?? "right"
                      }
                    />
                  );
                })}
              </BaseTableRow>
            </BaseTableHeader>
            <BaseTableBody>
              {rows.map((row) => {
                const rowId = String(row.id);
                const recordId = String(row.recordId);
                const cells = new Map(
                  objectArray(row.cells).map((cell) => [
                    String(cell.columnId),
                    cell,
                  ]),
                );
                return (
                  <BaseTableRow key={rowId} data-row-id={rowId}>
                    <BaseTableCell>
                      {translatedCopy(
                        row.label,
                        row.labelKey,
                        translate,
                        rowId,
                      )}
                    </BaseTableCell>
                    {columns.map((column) => {
                      const columnId = String(column.id);
                      const cell = cells.get(columnId);
                      const value = cell?.value;
                      const displayValue =
                        stringValue(cell?.displayValue) ?? displayScalar(value);
                      const isSelected =
                        selectedCell?.rowId === rowId &&
                        selectedCell?.columnId === columnId;
                      return (
                        <BaseTableCell
                          key={columnId}
                          align={
                            (stringValue(column.align) as
                              | "left"
                              | "center"
                              | "right"
                              | undefined) ?? "right"
                          }
                        >
                          <BaseButton
                            data-trend-cell
                            data-row-id={rowId}
                            data-column-id={columnId}
                            variant="ghost"
                            active={isSelected}
                            label={displayValue || "—"}
                            ariaLabel={translate(
                              DESIGN_CAPABILITY_I18N_KEYS.ontologyTrendMatrix
                                .select,
                              {
                                row: rowId,
                                column: columnId,
                                value: displayValue,
                              },
                            )}
                            onKeyDown={moveTrendCellFocus}
                            onClick={() =>
                              emitIntent(
                                "capability.ontology-trend-matrix.select",
                                {
                                  recordId,
                                  rowId,
                                  columnId,
                                  value: jsonScalar(value),
                                },
                              )
                            }
                          />
                        </BaseTableCell>
                      );
                    })}
                  </BaseTableRow>
                );
              })}
            </BaseTableBody>
          </BaseTable>
        </BaseTableContainer>
        <DataExplorerCollectionFooter
          selectedCount={0}
          loadedStart={rows.length > 0 ? rowOffset + 1 : 0}
          loadedEnd={Math.min(rowOffset + rows.length, totalRows)}
          total={totalRows}
          pageSize={pageSize}
          pageSizeOptions={[20, 50, 100, 200, 500, 1000]}
          tone="hard-edge"
          onPageSizeChange={(nextPageSize) =>
            emitIntent("capability.ontology-trend-matrix.page-size-change", {
              pageSize: nextPageSize,
            })
          }
        />
        {childrenBySlot.footer}
      </BaseStack>
    );
  };

type ContextField = (typeof contextFieldNames)[number];

const contextFieldLabel = (
  field: ContextField,
  translate: CapabilityTranslate,
) => translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.fields[field]);

const listSummary = (items: string[], translate: CapabilityTranslate) => {
  const visible = items.slice(0, MAX_VISIBLE_CONTEXT_VALUES).join(", ");
  const remaining = Math.max(0, items.length - MAX_VISIBLE_CONTEXT_VALUES);
  return remaining
    ? `${visible} ${translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.more, { count: remaining })}`
    : visible;
};

const dashboardSummary = (
  dashboard: JsonObject,
  translate: CapabilityTranslate,
) => {
  const identity =
    stringValue(dashboard.clickedLabel) ??
    stringValue(dashboard.dashboardId) ??
    stringValue(dashboard.sourceWidgetId) ??
    translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.dashboardContext);
  const countEntries = (value: JsonValue | undefined) =>
    Array.isArray(value)
      ? value.length
      : isJsonObject(value)
        ? Object.keys(value).length
        : 0;
  const explicitCount = [
    dashboard.scopedFilterCount,
    dashboard.globalFilterCount,
    dashboard.widgetScopedFilterCount,
    dashboard.variableCount,
  ].reduce(
    (total: number, value) =>
      total + (typeof value === "number" && Number.isFinite(value) ? value : 0),
    0,
  );
  const count =
    explicitCount ||
    [
      dashboard.scopedFilters,
      dashboard.globalFilters,
      dashboard.widgetScopedFilters,
      dashboard.variables,
    ].reduce<number>((total, value) => total + countEntries(value), 0);
  return count > 0
    ? `${identity} · ${translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.contextCount, { count })}`
    : identity;
};

interface ContextChip {
  field: ContextField;
  summary: string;
}

interface ContextTagChoice {
  id: string;
  label: string;
  disabled: boolean;
  color?: string;
  groupId?: string;
  groupLabel?: string;
}

interface ContextTagPanelGroup {
  id: string;
  label: string;
  description?: string;
  selectMode: "single" | "multiple";
  tags: ContextTagChoice[];
}

interface ContextTagPanel {
  id: string;
  label: string;
  description?: string;
  groups: ContextTagPanelGroup[];
}

const contextTagChoices = (
  value: JsonValue | undefined,
  translate: CapabilityTranslate,
): ContextTagChoice[] =>
  objectArray(value).flatMap((choice) => {
    const id = stringValue(choice.id);
    if (!id) return [];
    return [
      {
        id,
        label:
          stringValue(choice.label) ??
          (stringValue(choice.labelKey)
            ? translate(String(choice.labelKey))
            : id),
        disabled: choice.disabled === true,
        ...(stringValue(choice.color)
          ? { color: stringValue(choice.color) }
          : {}),
        ...(stringValue(choice.groupId)
          ? { groupId: stringValue(choice.groupId) }
          : {}),
        ...(stringValue(choice.groupLabel)
          ? { groupLabel: stringValue(choice.groupLabel) }
          : {}),
      },
    ];
  });

const contextTagPanels = (
  value: JsonValue | undefined,
  translate: CapabilityTranslate,
): ContextTagPanel[] =>
  objectArray(value).flatMap((panel) => {
    const id = stringValue(panel.id);
    if (!id) return [];
    const groups = objectArray(panel.groups).flatMap((group) => {
      const groupId = stringValue(group.id);
      if (!groupId) return [];
      return [
        {
          id: groupId,
          label: stringValue(group.label) ?? groupId,
          ...(stringValue(group.description)
            ? { description: stringValue(group.description) }
            : {}),
          selectMode: group.selectMode === "single" ? "single" : "multiple",
          tags: contextTagChoices(group.tags, translate),
        } satisfies ContextTagPanelGroup,
      ];
    });
    return [
      {
        id,
        label: stringValue(panel.label) ?? id,
        ...(stringValue(panel.description)
          ? { description: stringValue(panel.description) }
          : {}),
        groups,
      },
    ];
  });

const readableForeground = (color: string): string => {
  const match = color.trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return "white";
  const red = Number.parseInt(match[1].slice(0, 2), 16);
  const green = Number.parseInt(match[1].slice(2, 4), 16);
  const blue = Number.parseInt(match[1].slice(4, 6), 16);
  return red * 0.299 + green * 0.587 + blue * 0.114 > 160 ? "black" : "white";
};

const panelTagStyle = (
  choice: ContextTagChoice,
  selected: boolean,
): CSSProperties | undefined => {
  const color = choice.color?.trim();
  if (!color) return undefined;
  return selected
    ? {
        backgroundColor: color,
        borderColor: color,
        color: readableForeground(color),
      }
    : { backgroundColor: "transparent", borderColor: color, color };
};

interface LegacyDataExplorerContextFilterProps {
  model: JsonObject;
  translate: CapabilityTranslate;
  emitIntent: (type: string, payload: JsonObject) => void;
  snapshot: () => JsonObject;
  clearTags: () => void;
  triggerLabel: string;
  triggerAriaLabel: string;
  disabled: boolean;
}

const LegacyDataExplorerContextFilter = ({
  model,
  translate,
  emitIntent,
  snapshot,
  clearTags,
  triggerLabel,
  triggerAriaLabel,
  disabled,
}: LegacyDataExplorerContextFilterProps) => {
  const governedTagIds = stringArray(model.tagIds);
  const governedTagIdentity = governedTagIds.join("\u0000");
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<"view" | "all">(
    model.viewScoped === true ? "view" : "all",
  );
  const [keyword, setKeyword] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState(governedTagIds);
  const allChoices = useMemo(
    () => contextTagChoices(model.tagChoices, translate),
    [model.tagChoices, translate],
  );
  const viewChoices = useMemo(
    () => contextTagChoices(model.viewTagChoices, translate),
    [model.viewTagChoices, translate],
  );
  const panels = useMemo(
    () => contextTagPanels(model.tagPanelChoices, translate),
    [model.tagPanelChoices, translate],
  );
  const [selectedPanelId, setSelectedPanelId] = useState("");

  useEffect(() => {
    setSelectedTagIds(governedTagIds);
  }, [governedTagIdentity]);

  useEffect(() => {
    if (!panels.length) {
      setSelectedPanelId("");
      return;
    }
    if (!panels.some((panel) => panel.id === selectedPanelId)) {
      setSelectedPanelId(panels[0].id);
    }
  }, [panels, selectedPanelId]);

  const availableChoices =
    scope === "view" && model.viewScoped === true ? viewChoices : allChoices;
  const selectedSet = new Set(selectedTagIds);
  const declaredIds = new Set(availableChoices.map(({ id }) => id));
  const selectedFallbackChoices: ContextTagChoice[] = selectedTagIds
    .filter((id) => !declaredIds.has(id))
    .map((id) => ({ id, label: id, disabled: false }));
  const visibleChoices: ContextTagChoice[] = [
    ...availableChoices,
    ...selectedFallbackChoices,
  ].filter((choice) => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase();
    if (!normalizedKeyword) return true;
    return [choice.id, choice.label, choice.groupLabel]
      .filter(Boolean)
      .some((value) =>
        String(value).toLocaleLowerCase().includes(normalizedKeyword),
      );
  });
  const visibleTagIds = new Set(availableChoices.map(({ id }) => id));
  const activePanel = panels.find(({ id }) => id === selectedPanelId);
  const visiblePanelGroups = (activePanel?.groups ?? [])
    .map((group) => ({
      ...group,
      tags:
        scope === "view" && model.viewScoped === true
          ? group.tags.filter(({ id }) => visibleTagIds.has(id))
          : group.tags,
    }))
    .filter((group) => group.tags.length > 0);

  const changeTags = (nextTagIds: string[]) => {
    setSelectedTagIds(nextTagIds);
    emitIntent("capability.data-context-bar.change", {
      changedField: "tagIds",
      ...snapshot(),
      tagIds: nextTagIds,
    });
  };
  const toggleTag = (
    tagId: string,
    groupTagIds?: string[],
    selectMode: "single" | "multiple" = "multiple",
  ) => {
    if (disabled) return;
    if (selectMode === "single" && groupTagIds) {
      const withoutGroup = selectedTagIds.filter(
        (selectedId) => !groupTagIds.includes(selectedId),
      );
      changeTags(
        selectedSet.has(tagId) ? withoutGroup : [...withoutGroup, tagId],
      );
      return;
    }
    changeTags(
      selectedSet.has(tagId)
        ? selectedTagIds.filter((selectedId) => selectedId !== tagId)
        : [...selectedTagIds, tagId],
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setKeyword("");
          setScope(model.viewScoped === true ? "view" : "all");
          setSelectedTagIds(governedTagIds);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 shrink-0 px-2 2xl:px-3"
          aria-label={triggerAriaLabel}
          title={triggerLabel}
          disabled={disabled}
        >
          <Filter className="h-4 w-4 2xl:mr-2" />
          <span className="sr-only 2xl:not-sr-only">{triggerLabel}</span>
          {selectedTagIds.length > 0 ? (
            <Badge variant="secondary" className="ml-0 px-2 py-0.5 2xl:ml-2">
              {selectedTagIds.length}
            </Badge>
          ) : null}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-4xl overflow-hidden"
        data-legacy-data-explorer-tag-filter
      >
        <DialogHeader>
          <DialogTitle>{triggerLabel}</DialogTitle>
          <DialogDescription>
            {translate(
              DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.filterDescription,
            )}
          </DialogDescription>
        </DialogHeader>
        {model.viewScoped === true ? (
          <div className="flex min-w-0 items-center justify-between gap-3 border-b pb-3">
            <span className="text-sm font-medium">
              {translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.tagScope)}
            </span>
            <div
              className="inline-flex h-9 items-center rounded-md border bg-muted/40 p-1"
              role="group"
              aria-label={translate(
                DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.tagScope,
              )}
            >
              <Button
                type="button"
                size="sm"
                variant={scope === "view" ? "secondary" : "ghost"}
                className="h-7 px-3"
                aria-pressed={scope === "view"}
                onClick={() => setScope("view")}
              >
                {translate(
                  DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.currentViewTags,
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={scope === "all" ? "secondary" : "ghost"}
                className="h-7 px-3"
                aria-pressed={scope === "all"}
                onClick={() => setScope("all")}
              >
                {translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.allTags)}
              </Button>
            </div>
          </div>
        ) : null}
        <Tabs defaultValue="all" className="w-full min-w-0">
          <TabsList className="grid w-full min-w-0 grid-cols-2">
            <TabsTrigger value="panel">
              {translate(
                DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.panelFilter,
              )}
            </TabsTrigger>
            <TabsTrigger value="all">
              {translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.allTags)}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="panel" className="min-w-0 pt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  {translate(
                    DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.tagPanel,
                  )}
                </div>
                <Select
                  value={selectedPanelId}
                  onValueChange={setSelectedPanelId}
                >
                  <SelectTrigger disabled={panels.length === 0}>
                    <SelectValue
                      placeholder={translate(
                        DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.selectPanel,
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {panels.map((panel) => (
                      <SelectItem key={panel.id} value={panel.id}>
                        {panel.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activePanel?.description ? (
                  <div className="text-xs text-muted-foreground">
                    {activePanel.description}
                  </div>
                ) : null}
              </div>
              <div className="h-40 overflow-y-auto rounded-md border p-3">
                {!activePanel ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {translate(
                      DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.noPanels,
                    )}
                  </div>
                ) : visiblePanelGroups.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {translate(
                      DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.noPanelGroups,
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {visiblePanelGroups.map((group) => {
                      const groupTagIds = group.tags.map(({ id }) => id);
                      return (
                        <div key={group.id} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {group.label}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {translate(
                                group.selectMode === "single"
                                  ? DESIGN_CAPABILITY_I18N_KEYS.dataContextBar
                                      .singleSelect
                                  : DESIGN_CAPABILITY_I18N_KEYS.dataContextBar
                                      .multiSelect,
                              )}
                            </Badge>
                          </div>
                          {group.description ? (
                            <div className="text-xs text-muted-foreground">
                              {group.description}
                            </div>
                          ) : null}
                          <div className="flex flex-wrap gap-3">
                            {group.tags.map((choice) => {
                              const selected = selectedSet.has(choice.id);
                              return (
                                <button
                                  key={choice.id}
                                  type="button"
                                  disabled={choice.disabled || disabled}
                                  onClick={() =>
                                    toggleTag(
                                      choice.id,
                                      groupTagIds,
                                      group.selectMode,
                                    )
                                  }
                                  className={badgeVariants({
                                    variant:
                                      selected && !choice.color
                                        ? "secondary"
                                        : "outline",
                                    className:
                                      "cursor-pointer select-none gap-1.5 text-xs font-medium disabled:pointer-events-none disabled:opacity-50",
                                  })}
                                  style={panelTagStyle(choice, selected)}
                                >
                                  <span>{choice.label}</span>
                                  <span className="text-[10px] leading-none opacity-80">
                                    {choice.groupLabel ?? group.label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="all" className="min-w-0 pt-4">
            <div className="flex min-h-0 w-full min-w-0 flex-col gap-3">
              <Input
                value={keyword}
                onChange={(event) => setKeyword(event.currentTarget.value)}
                placeholder={translate(
                  DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.searchTags,
                )}
                disabled={disabled}
              />
              <div className="h-40 w-full min-w-0 overflow-y-auto rounded-md border p-2">
                {visibleChoices.length === 0 ? (
                  <div className="py-4 text-center text-xs text-muted-foreground">
                    {translate(
                      DESIGN_CAPABILITY_I18N_KEYS.dataContextBar
                        .noAvailableTags,
                    )}
                  </div>
                ) : (
                  <div className="flex w-max flex-col gap-2 pr-2">
                    {visibleChoices.map((choice) => (
                      <label
                        key={choice.id}
                        className="grid w-max grid-cols-[auto_minmax(160px,max-content)_2rem_minmax(72px,max-content)_max-content] items-center gap-3 whitespace-nowrap text-sm"
                      >
                        <Checkbox
                          checked={selectedSet.has(choice.id)}
                          onCheckedChange={() => toggleTag(choice.id)}
                          disabled={choice.disabled || disabled}
                        />
                        <span className="whitespace-nowrap">
                          {choice.label}
                        </span>
                        <div className="w-0" />
                        <span className="shrink-0 rounded-full border bg-muted/40 px-2 py-0.5 text-[11px] leading-none text-muted-foreground">
                          {choice.groupLabel ??
                            translate(
                              DESIGN_CAPABILITY_I18N_KEYS.dataContextBar
                                .defaultGroup,
                            )}
                        </span>
                        <span className="shrink-0 rounded bg-muted/30 px-2 py-0.5 font-mono text-xs leading-none text-muted-foreground">
                          {choice.id}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {visibleChoices.length > 0 ? (
                  <div className="py-2 text-center text-xs text-muted-foreground">
                    {translate(
                      DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.loadedAll,
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() =>
              emitIntent("capability.data-context-bar.refresh", {})
            }
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.refreshTags)}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setSelectedTagIds([]);
              clearTags();
            }}
          >
            {translate(
              DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.tagFilterClear,
            )}
          </Button>
          <Button onClick={() => setOpen(false)}>
            {translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.confirm)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const contextChips = (
  model: JsonObject,
  translate: CapabilityTranslate,
): ContextChip[] => {
  const chips: ContextChip[] = [];
  for (const field of ["tagIds", "tagGroupIds"] as const) {
    const values = stringArray(model[field]);
    if (values.length)
      chips.push({ field, summary: listSummary(values, translate) });
  }
  for (const field of ["timeFrom", "timeTo", "clickedField"] as const) {
    const value = stringValue(model[field]);
    if (value)
      chips.push({ field, summary: value.slice(0, MAX_SAFE_TEXT_LENGTH) });
  }
  if (model.clickedValue !== undefined && model.clickedValue !== null)
    chips.push({
      field: "clickedValue",
      summary: displayJsonSummary(model.clickedValue),
    });
  if (
    model.dashboardDrilldown !== undefined &&
    model.dashboardDrilldown !== null
  )
    chips.push({
      field: "dashboardDrilldown",
      summary: isJsonObject(model.dashboardDrilldown)
        ? dashboardSummary(model.dashboardDrilldown, translate)
        : displayJsonSummary(model.dashboardDrilldown),
    });
  return chips;
};

export const DataContextBarCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
  emitIntent,
}) => {
  const search = typeof model.search === "string" ? model.search : "";
  const declaredEnabledFields = stringArray(model.enabledFields).filter(
    (field): field is ContextField =>
      contextFieldNames.includes(field as ContextField),
  );
  const enabledFields = new Set<ContextField>(
    model.enabledFields === undefined
      ? contextFieldNames
      : declaredEnabledFields,
  );
  const chips = contextChips(model, translate).filter(({ field }) =>
    enabledFields.has(field),
  );
  const snapshot = (): JsonObject => ({
    search,
    tagIds: stringArray(model.tagIds),
    tagGroupIds: stringArray(model.tagGroupIds),
    timeFrom: typeof model.timeFrom === "string" ? model.timeFrom : "",
    timeTo: typeof model.timeTo === "string" ? model.timeTo : "",
    clickedField:
      typeof model.clickedField === "string" ? model.clickedField : "",
    clickedValue: governedJsonValue(model.clickedValue),
    dashboardDrilldown: governedJsonValue(model.dashboardDrilldown),
  });
  const choiceOptions = (
    choiceField: "tagChoices" | "tagGroupChoices",
    selectedIds: string[],
  ) => {
    const declared = objectArray(model[choiceField]).flatMap((choice) => {
      const id = stringValue(choice.id);
      if (!id) return [];
      return [
        {
          value: id,
          label:
            stringValue(choice.label) ??
            (stringValue(choice.labelKey)
              ? translate(String(choice.labelKey))
              : id),
          disabled: choice.disabled === true,
        },
      ];
    });
    const ids = new Set(declared.map(({ value }) => value));
    return [
      ...declared,
      ...selectedIds
        .filter((id) => !ids.has(id))
        .map((id) => ({ value: id, label: id })),
    ];
  };
  const clearedSnapshot = (field: ContextField | "all"): JsonObject => {
    const next = snapshot();
    const fields = field === "all" ? contextFieldNames : [field];
    for (const target of fields) {
      next[target] =
        target === "tagIds" || target === "tagGroupIds"
          ? []
          : target === "clickedValue" || target === "dashboardDrilldown"
            ? null
            : "";
    }
    return next;
  };
  const clear = (field: ContextField | "all") =>
    emitIntent("capability.data-context-bar.clear", {
      clearedField: field,
      ...clearedSnapshot(field),
    });
  const compact =
    model.presentation === "compact" ||
    model.presentation === "legacy-data-explorer";
  const legacyDataExplorer = model.presentation === "legacy-data-explorer";
  if (
    !compact &&
    enabledFields.size === 0 &&
    !childrenBySlot.leading &&
    !childrenBySlot.content &&
    !childrenBySlot.actions
  ) {
    return null;
  }
  const filterControls = (
    <>
      {enabledFields.has("search") ? (
        <BaseInput
          type="search"
          value={search}
          aria-label={translate(
            DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.searchLabel,
          )}
          placeholder={translate(
            DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.searchPlaceholder,
          )}
          onChange={(event) =>
            emitIntent("capability.data-context-bar.change", {
              changedField: "search",
              ...snapshot(),
              search: event.currentTarget.value,
            })
          }
        />
      ) : null}
      {enabledFields.has("tagIds") ? (
        <BaseField label={contextFieldLabel("tagIds", translate)}>
          <BaseMultiSelect
            aria-label={contextFieldLabel("tagIds", translate)}
            placeholder={contextFieldLabel("tagIds", translate)}
            options={choiceOptions("tagChoices", stringArray(model.tagIds))}
            value={stringArray(model.tagIds)}
            onValueChange={(tagIds) =>
              emitIntent("capability.data-context-bar.change", {
                changedField: "tagIds",
                ...snapshot(),
                tagIds,
              })
            }
          />
        </BaseField>
      ) : null}
      {enabledFields.has("tagGroupIds") ? (
        <BaseField label={contextFieldLabel("tagGroupIds", translate)}>
          <BaseMultiSelect
            aria-label={contextFieldLabel("tagGroupIds", translate)}
            placeholder={contextFieldLabel("tagGroupIds", translate)}
            options={choiceOptions(
              "tagGroupChoices",
              stringArray(model.tagGroupIds),
            )}
            value={stringArray(model.tagGroupIds)}
            onValueChange={(tagGroupIds) =>
              emitIntent("capability.data-context-bar.change", {
                changedField: "tagGroupIds",
                ...snapshot(),
                tagGroupIds,
              })
            }
          />
        </BaseField>
      ) : null}
      {(["timeFrom", "timeTo"] as const)
        .filter((field) => enabledFields.has(field))
        .map((field) => (
          <BaseField key={field} label={contextFieldLabel(field, translate)}>
            <BaseInput
              type="text"
              aria-label={contextFieldLabel(field, translate)}
              value={typeof model[field] === "string" ? model[field] : ""}
              onChange={(event) =>
                emitIntent("capability.data-context-bar.change", {
                  changedField: field,
                  ...snapshot(),
                  [field]: event.currentTarget.value,
                })
              }
            />
          </BaseField>
        ))}
      <BaseInline gap="var(--global-spacing)" role="list">
        {chips.map(({ field, summary }) => {
          const label = contextFieldLabel(field, translate);
          return (
            <span key={field} role="listitem">
              <BaseBadge label={`${label}: ${summary}`} tone="muted" />
              <BaseButton
                variant="ghost"
                size="sm"
                label={translate(
                  DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.clear,
                )}
                ariaLabel={`${translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.clear)} ${label}`}
                onClick={() => clear(field)}
              />
            </span>
          );
        })}
      </BaseInline>
      {childrenBySlot.content}
    </>
  );
  const clearAllButton =
    chips.length || search ? (
      <BaseButton
        variant="ghost"
        label={translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.clearAll)}
        onClick={() => clear("all")}
      />
    ) : null;

  if (compact) {
    const activeCount =
      chips.length + (enabledFields.has("search") && search ? 1 : 0);
    const declaredLabelKey = stringValue(model.labelKey);
    const triggerLabel = declaredLabelKey
      ? translate(declaredLabelKey)
      : `${translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.filter)}${
          activeCount ? ` (${activeCount})` : ""
        }`;
    const declaredAriaLabelKey = stringValue(model.ariaLabelKey);
    const triggerAriaLabel = declaredAriaLabelKey
      ? translate(declaredAriaLabelKey)
      : declaredLabelKey
        ? translate(declaredLabelKey)
        : triggerLabel;
    return (
      <BaseInline
        gap="var(--global-spacing)"
        aria-label={translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.label)}
      >
        {childrenBySlot.leading}
        {legacyDataExplorer ? (
          <LegacyDataExplorerContextFilter
            model={model}
            translate={translate}
            emitIntent={emitIntent}
            snapshot={snapshot}
            clearTags={() => clear("tagIds")}
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            disabled={model.disabled === true || !enabledFields.has("tagIds")}
          />
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <BaseButton
                label={triggerLabel}
                ariaLabel={triggerAriaLabel}
                variant="secondary"
                disabled={model.disabled === true || enabledFields.size === 0}
              />
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="max-h-[min(70vh,40rem)] w-80 overflow-y-auto"
            >
              <BaseStack gap="var(--global-spacing)">
                {filterControls}
                {clearAllButton}
              </BaseStack>
            </PopoverContent>
          </Popover>
        )}
        {childrenBySlot.actions}
      </BaseInline>
    );
  }

  return (
    <BaseToolbar
      aria-label={translate(DESIGN_CAPABILITY_I18N_KEYS.dataContextBar.label)}
      leading={childrenBySlot.leading}
      actions={
        <>
          {clearAllButton}
          {childrenBySlot.actions}
        </>
      }
    >
      {filterControls}
    </BaseToolbar>
  );
};
